/*
 * PROD-7828 R8 — the re-entry guard's memory. After an IDEMPOTENCY_MISMATCH the saved entry is kept
 * in sessionStorage (this browser tab only), so switching tabs, changing month or reloading cannot
 * drop the question "you already saved this teacher — add another?". Entries carry their own
 * teacher_uuid + pay_month; the tab filters by both. Every storage touch is guarded: without
 * storage the guard still works for the life of the mounted tab.
 */
export const SAVED_GUARD_KEY = 'tp.additional.saved_entries.v1';
export const SAVED_GUARD_MAX = 50;

// Reading window.sessionStorage itself throws in some privacy modes, so even the lookup is guarded.
const session_storage = () => window.sessionStorage;

const is_entry = (e) =>
  !!e &&
  typeof e === 'object' &&
  typeof e.uuid === 'string' &&
  typeof e.teacher_uuid === 'string' &&
  typeof e.pay_month === 'string';

export const read_saved_guard = (get_storage = session_storage) => {
  try {
    const storage = get_storage();
    const raw = storage ? storage.getItem(SAVED_GUARD_KEY) : null;
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list.filter(is_entry) : [];
  } catch (e) {
    return [];
  }
};

// false when the entries could not be stored; the in-memory copy is then the only guard.
export const write_saved_guard = (entries, get_storage = session_storage) => {
  try {
    const storage = get_storage();
    if (!storage) return false;
    storage.setItem(SAVED_GUARD_KEY, JSON.stringify(entries));
    return true;
  } catch (e) {
    return false;
  }
};

export const remember_saved_entry = (list, row) =>
  is_entry(row) ? [...list.filter((x) => x.uuid !== row.uuid), row].slice(-SAVED_GUARD_MAX) : list;

export const forget_saved_entry = (list, uuid) => list.filter((x) => x.uuid !== uuid);

// The list's copy is fresher (version, actions, a removal elsewhere); same list back when nothing matched.
export const refresh_saved_entries = (list, rows) => {
  const by_uuid = new Map(
    (Array.isArray(rows) ? rows : []).filter(is_entry).map((r) => [r.uuid, r])
  );
  if (!list.some((x) => by_uuid.has(x.uuid))) return list;
  return list.map((x) => by_uuid.get(x.uuid) || x);
};
